import axios from "axios";
import { MAIN_API } from "./stepoutAPI";
import apiClient from "../utils/axiosClient";

/**
 * Verify and refresh token if invalid
 * @param {string} refreshToken - Refresh token to verify and get new access token
 * @returns {Promise<{valid: boolean, newToken?: string, logout?: boolean}>}
 */
export const verifyToken = async (refreshToken) => {
  try {
    const response = await axios({
      method: "POST",
      url: `${MAIN_API}verify-token-analyst/`,
      data: {
        token: refreshToken,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      return {
        valid: true,
        newToken: response?.data?.token || refreshToken,
      };
    }
  } catch (error) {
    // if (error.response?.status === 403) {
    //   // Check if the error message is INVALID_TOKEN
    //   if (error.response?.data?.msg === "INVALID_TOKEN" || error.response?.data?.msg === "NO_TOKEN_OR_SERVER_ERROR") {
    //     return {
    //       valid: false,
    //       newToken: null,
    //       logout: true, // Signal to logout the user
    //     };
    //   }
    //   return {
    //     valid: false,
    //     newToken: null,
    //     logout: false,
    //   };
    // }
    throw error;
  }
};

/**
 * Setup axios interceptor for token refresh
 * This intercepts 403 responses and attempts to refresh the token
 */
// Builds the 403-refresh handler bound to the axios instance that made the
// request, so retries go back through the same instance (global or apiClient).
const makeResponseErrorHandler = (instance) => async (error) => {
  const originalRequest = error.config;

  // Never try to refresh the verify/refresh call itself — otherwise a 403 from
  // verify-token-analyst would re-enter this handler and loop forever.
  const url = originalRequest?.url || "";
  if (url.includes("verify-token-analyst")) {
    return Promise.reject(error);
  }

  // Check if error is 403 (Forbidden - Invalid Token)
  if (error.response?.status === 403 && originalRequest && !originalRequest._retry) {
    originalRequest._retry = true;

    return 

        try {
          // Get refresh token from sessionStorage
          const refreshToken = sessionStorage.getItem("refresh_token");
          const currentToken = sessionStorage.getItem("token");

          if (!refreshToken) {
            // No refresh token available, redirect to login
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("refresh_token");
            window.location.href = "/login";
            return Promise.reject(error);
          }

          // Verify and refresh token using refresh token
          const { valid, newToken, logout } = await verifyToken(refreshToken);

          if (logout) {
            // Token is invalid and user should be logged out
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("refresh_token");
            window.location.href = "/login";
            return Promise.reject(error);
          }

          if (valid && newToken && newToken !== currentToken) {
            // Update token in storage with the new token
            sessionStorage.setItem("token", newToken);

            // Update authorization header with new token
            originalRequest.headers.Authorization = "ANALYST " + newToken;

            // Retry original request with new token
            return instance(originalRequest);
          } else if (valid && newToken === currentToken) {
            // Token validation successful but no new token returned
            // This means token is still valid, retry the request
            return instance(originalRequest);
          } else {
            // Token is invalid and cannot be refreshed
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("refresh_token");
            window.location.href = "/login";
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // Error during token refresh process
          console.error("Token refresh failed:", refreshError);
          sessionStorage.removeItem("token");
          sessionStorage.removeItem("refresh_token");
          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
  }

  // For other errors, just reject
  return Promise.reject(error);
};

let _installed = false;

/**
 * Setup axios interceptor for token refresh.
 * Registered on BOTH the global axios and the shared apiClient instance —
 * interceptors on the default instance do not apply to axios.create() instances.
 * Idempotent + invoked at module load so it's active before any component
 * effect fires a request (child effects run before the App effect).
 */
export const setupAxiosInterceptor = () => {
  if (_installed) return;
  _installed = true;
  console.log("Setting up axios interceptor for token refresh...");
  axios.interceptors.response.use((response) => response, makeResponseErrorHandler(axios));
  apiClient.interceptors.response.use((response) => response, makeResponseErrorHandler(apiClient));
};

// Install immediately on import (before React mounts / any effect runs).
setupAxiosInterceptor();
